/**
 * Every tunable number in the app, in one place.
 *
 * If a value appears in more than one file, or a reader would have to guess why
 * it is what it is, it belongs here rather than inline at the point of use.
 * User-facing wording lives in content.ts; colours and sizing live in styles.css.
 */
import type { ItemPeriod, TimePeriod } from "./types";

// ---------- Units ----------

/** UK (imperial) gallon, which is what "mpg" means here. */
export const LITRES_PER_GALLON = 4.54609;
export const KM_PER_MILE = 1.609344;
/** Minor units in a major one: 100p to the pound, 100c to the euro. */
export const MINOR_PER_MAJOR = 100;
/** Number formatting locale. Both supported currencies are formatted the same way. */
export const LOCALE = "en-GB";

export const TIME_PERIODS: readonly TimePeriod[] = ["year", "month", "week", "day"];
export const ITEM_PERIODS: readonly ItemPeriod[] = [...TIME_PERIODS, "distance"];
/** How many times a period occurs in a year. */
export const PERIOD_MULT: Record<TimePeriod, number> = { year: 1, month: 12, week: 52, day: 365 };

// ---------- Input limits ----------
// Names have to fit tab chips and cards; numbers have to fit their fields.

export const MAX_VEHICLE_NAME = 32;
export const MAX_PROFILE_NAME = 24;
export const MAX_COST_NAME = 30;
/** Cost amounts. */
export const MAX_AMOUNT = 99999.99;
/** Fuel and energy unit prices. */
export const MAX_PRICE = 9999.99;
/** mpg, km/L, mi/kWh. */
export const MAX_ECONOMY = 999.99;
/** Distance per period. */
export const MAX_DISTANCE = 999999;
/** Smallest amount a field will accept: anything lower reads as "not entered yet". */
export const MIN_POSITIVE = 0.01;
/** Decimal places kept for money, prices and economy figures. */
export const DECIMALS_MONEY = 2;
/** Distances are whole numbers. */
export const DECIMALS_DISTANCE = 0;

// ---------- Defaults ----------

/**
 * Starting figures for a vehicle the person has just added. They are plausible UK
 * averages rather than facts, so each is flagged "needs completing" until edited.
 */
export const NEW_VEHICLE_DEFAULTS = {
  finance: 200,
  service: 150,
  mot: 45,
  roadTax: 190,
  insurance: 500,
  mpg: 45,
  iceHomePrice: 143.9,
  iceAwayPrice: 151.5,
  evMiPerKwh: 3.8,
  evHomeRate: 8,
  evAwayRate: 70,
} as const;

/** Fixed costs seeded into an example vehicle. A 0 means "doesn't apply here". */
export interface ExampleCosts {
  finance: number;
  service: number;
  mot: number;
  roadTax: number;
  roadTaxPerDistance: number;
  insurance: number;
}

export interface ExampleVehicle {
  costs: ExampleCosts;
  mpg: number;
  iceHomePrice: number;
  iceAwayPrice: number;
  evMiPerKwh: number;
  evHomeRate: number;
  evAwayRate: number;
}

/** The two worked examples a first-time visitor sees, in miles and pounds. */
export const EXAMPLE_VEHICLES: Record<"petrol" | "electric", ExampleVehicle> = {
  petrol: {
    costs: { finance: 240, service: 150, mot: 45, roadTax: 190, roadTaxPerDistance: 0, insurance: 620 },
    mpg: 48,
    iceHomePrice: 143.9,
    iceAwayPrice: 151.5,
    evMiPerKwh: 3.5,
    evHomeRate: 8,
    evAwayRate: 79,
  },
  electric: {
    costs: { finance: 310, service: 120, mot: 45, roadTax: 0, roadTaxPerDistance: 3.5, insurance: 540 },
    mpg: 45,
    iceHomePrice: 143.9,
    iceAwayPrice: 151.5,
    evMiPerKwh: 3.8,
    evHomeRate: 7.5,
    evAwayRate: 68,
  },
};

export const EXAMPLE_PROFILES = [
  { distanceAmount: 8000, homeShare: 80 },
  { distanceAmount: 15000, homeShare: 45 },
] as const;

/** A profile added by hand starts here. */
export const NEW_PROFILE_DEFAULTS = { distanceAmount: 10000, period: "year" as TimePeriod, homeShare: 60 };

// ---------- Storage ----------

export const IDB_NAME = "carcost";
export const IDB_VERSION = 1;
export const IDB_STORE = "state";
export const IDB_KEY = "app";
/** Only used when IndexedDB is unavailable, e.g. some private-browsing modes. */
export const LOCAL_STORAGE_KEY = "carcost-state-v1";
export const THEME_STORAGE_KEY = "carcost-theme";

// ---------- Import / export ----------

/** Identifies our own export files. Unchanged from the original prototype so its exports still import. */
export const EXPORT_APP_ID = "cost-per-mile";
export const EXPORT_VERSION = 1;
/** Caps how much work one imported file can create. */
export const MAX_IMPORT_ENTRIES = 50;
export const MAX_IMPORT_BYTES = 1024 * 1024;
/** Longest id accepted from a file, to keep an imported key from bloating storage. */
export const MAX_ID_LENGTH = 64;
/** Long enough for the browser to have started the download before the URL is released. */
export const OBJECT_URL_LIFETIME_MS = 2000;

// ---------- Layout and presentation ----------

/**
 * Phone breakpoint. The same 480px appears in the responsive section of
 * styles.css and in public/theme-init.js; changing it means changing all three.
 */
export const MOBILE_MAX_WIDTH = 480;
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;
/** Below this share of the bar a segment is too narrow to hold its label legibly. */
export const SEGMENT_LABEL_MIN_PCT = 12;
/** Number of distinct vehicle colours defined as --vehicle-1..n in styles.css. */
export const VEHICLE_COLOR_COUNT = 6;
/** Keeps the selected tab clear of the scroll area's edges when it is scrolled into view. */
export const TAB_SCROLL_PADDING = { left: 32, right: 16 };
/** Gap between the help "?" and its tip, and the smallest gap to the viewport edge. */
export const TOOLTIP_GAP = 10;
export const TOOLTIP_EDGE_MARGIN = 8;
